import { create } from 'zustand';
import type { Unsubscribe } from '@firebase/firestore';
import { MapOverlay } from '../types';
import { mapOverlaysApi } from '../api/mapOverlays';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('mapOverlaysStore');

// Module-level subscription handle, same pattern as defensePlansStore's
// ownPlanUnsub/ownPlanKey: a zustand store's own state isn't a safe place to
// stash an Unsubscribe closure across StrictMode double-invocation. Keyed
// only on the moderator claim so a repeat subscribe() call with the same
// argument is a no-op rather than tearing down and re-attaching a live
// listener.
let overlaysUnsub: Unsubscribe | null = null;
let overlaysKey: string | null = null;

function keyFor(moderator: boolean): string {
  return moderator ? 'all' : 'shared';
}

function byId(overlays: MapOverlay[]): Record<string, MapOverlay> {
  return Object.fromEntries(overlays.map((o) => [o.id, o]));
}

interface MapOverlaysState {
  overlays: MapOverlay[];
  overlaysById: Record<string, MapOverlay>;
  loading: boolean;
  error: string | null;
  // Optimistically hidden ahead of a deferred deleteOverlay commit
  // (panel-system.md Phase 4's undo toast for "Remove <overlay>" — see
  // MapLayersContent's handleRemovePlanMap and
  // src/utils/deferredCommit.ts). Mirrors defensePlansStore's
  // hiddenPartyIds: the overlay disappears from the Plan maps list the
  // moment a moderator removes it, six seconds before the real delete goes
  // out, so Undo can restore it with no round trip.
  hiddenOverlayIds: Record<string, true>;
  hideOverlay: (overlayId: string) => void;
  unhideOverlay: (overlayId: string) => void;

  // Chooses subscribeAllOverlays (draft + shared) for a moderator, else
  // subscribeSharedOverlays. Both are global — every overlay the reader may
  // see, not just those in one area. Repeat calls with the same moderator
  // value reuse the live listener; a flip tears down the old one first.
  // Returns the Unsubscribe so a caller (e.g. useMapSubscriptions) can also
  // tear it down on unmount — reset() below is the sign-out backstop for
  // callers that don't.
  subscribe: (opts: { moderator: boolean }) => Unsubscribe;

  reset: () => void;
}

export const useMapOverlaysStore = create<MapOverlaysState>((set) => ({
  overlays: [],
  overlaysById: {},
  loading: false,
  error: null,
  hiddenOverlayIds: {},
  hideOverlay: (overlayId) =>
    set((s) => ({ hiddenOverlayIds: { ...s.hiddenOverlayIds, [overlayId]: true } })),
  unhideOverlay: (overlayId) =>
    set((s) => {
      if (!(overlayId in s.hiddenOverlayIds)) return s;
      const next = { ...s.hiddenOverlayIds };
      delete next[overlayId];
      return { hiddenOverlayIds: next };
    }),

  subscribe: ({ moderator }) => {
    const key = keyFor(moderator);
    if (overlaysUnsub && overlaysKey === key) return overlaysUnsub;
    overlaysUnsub?.();
    overlaysKey = key;
    set({ loading: true, error: null });

    const onNext = (overlays: MapOverlay[]) =>
      set({ overlays, overlaysById: byId(overlays), loading: false });
    const onError = (error: Error) => {
      log.warn('subscribe failed:', error);
      set({ loading: false, error: error.message });
    };

    overlaysUnsub = moderator
      ? mapOverlaysApi.subscribeAllOverlays(onNext, onError)
      : mapOverlaysApi.subscribeSharedOverlays(onNext, onError);
    return overlaysUnsub;
  },

  reset: () => {
    overlaysUnsub?.();
    overlaysUnsub = null;
    overlaysKey = null;
    set({ overlays: [], overlaysById: {}, loading: false, error: null, hiddenOverlayIds: {} });
  },
}));
